import classNames from 'classnames';
import React, { useState } from 'react';
import type { Accept } from 'react-dropzone';
import type { DisplayMode } from './BannerImageList';
import BannerImageList, { BannerImageListButton } from './BannerImageList';
import * as styles from './BannerModalContent.module.scss';
import Button from './Button';
import type { ImageLibrary } from './imageLibraries';
import StickyHeader from './StickyHeader';
import Tabs, { Tab } from './Tabs';
import UnsplashPicker from './UnsplashPicker';
import Uploader from './Uploader';

export type Banner = {
  image: FormDataEntryValue;
  unsplashId?: string;
};

type TabType = 'library' | 'upload' | 'unsplash';

interface BannerModalContentProps {
  initialTab?: TabType;
  onRemove(): void;
  onBannerChange: (newBanner: Banner | null) => void;
  imageLibrary: ImageLibrary;
  displayMode: DisplayMode;
  uploadInfo?: React.ReactNode;
  uploaderAccept: Accept;
}

const BannerModalContent = ({
  onBannerChange,
  initialTab = 'library',
  onRemove,
  imageLibrary,
  displayMode,
  uploadInfo,
  uploaderAccept,
}: BannerModalContentProps) => {
  const [tab, setTab] = useState<TabType>(initialTab);

  return (
    <div className={classNames(styles.container, styles[tab])}>
      <StickyHeader>
        <div>
          <Tabs
            right={
              <div className={styles.removeButtonContainer}>
                <Button variant="secondary" onClick={onRemove}>
                  Remove
                </Button>
              </div>
            }
          >
            <Tab onClick={() => setTab('library')} active={tab === 'library'}>
              Library
            </Tab>
            <Tab onClick={() => setTab('upload')} active={tab === 'upload'}>
              Upload
            </Tab>
            <Tab onClick={() => setTab('unsplash')} active={tab === 'unsplash'}>
              Unsplash
            </Tab>
          </Tabs>
        </div>
      </StickyHeader>
      {tab === 'library' && (
        <div className={styles.library}>
          {imageLibrary.map((libraryGroup) => (
            <div key={libraryGroup.name}>
              <div className={styles.libraryGroupName}>{libraryGroup.name}</div>
              <BannerImageList displayMode={displayMode}>
                {libraryGroup.images.map(({ alt, image }) => (
                  <BannerImageListButton
                    key={alt}
                    onClick={() => onBannerChange({ image })}
                    alt={alt}
                    src={image}
                  />
                ))}
              </BannerImageList>
            </div>
          ))}
        </div>
      )}
      {tab === 'upload' && (
        <div className={styles.uploadContainer}>
          <Uploader
            accept={uploaderAccept}
            onDrop={(files: File[]) => {
              if (files[0]) {
                onBannerChange({ image: files[0] });
              }
            }}
            image
            subject="an image"
            info={uploadInfo}
            fullHeight
          />
        </div>
      )}
      {tab === 'unsplash' && (
        <UnsplashPicker displayMode={displayMode} onPick={onBannerChange} />
      )}
    </div>
  );
};

export default BannerModalContent;
